import { telefonoParaMostrar } from "../../helpers/telefonoWhatsapp";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { useClientesApi } from "./clientesApi";
import FusionarFichasModal from "./FusionarFichasModal";
import { CONSENT_FILTROS, CONSENT_LABEL, errorDeApi, estadoConsent, fechaAR, infoDe } from "./clientesUtils";
import "../reservas/reservas-organizer.css";
import "./clientes.css";

/* ─────────────────────────────────────────
   ClientesLista — "Clientes" del salón: búsqueda (con demora), filtros "cumplen este mes" y "posibles duplicados",
   paginado del servidor y fusión de las fichas seleccionadas (la selección se conserva entre páginas y se borra al
   cambiar la búsqueda o los filtros).
   "Archivados" (like WhatsApp): an entry at the top of the list opens the archived clients, with the same search and
   paging; there the selection is unarchived instead of merged.
   Los profesionales vinculados reciben del back solo los clientes que atendieron.
───────────────────────────────────────── */

const DEMORA_BUSQUEDA = 300;

const ClientesLista = ({ onAbrir, refreshKey = 0 }) => {
  const api = useClientesApi();
  const [texto, setTexto] = useState("");
  const [q, setQ] = useState("");
  const [cumpleMes, setCumpleMes] = useState(false);
  const [duplicados, setDuplicados] = useState(false);
  const [consent, setConsent] = useState("");
  const [seleccion, setSeleccion] = useState({});
  const [fusionando, setFusionando] = useState(false);
  const [archivados, setArchivados] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [errorFusion, setErrorFusion] = useState("");
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const pedido = useRef(0);

  // The server query waits for the typing to stop; a new search always goes back to the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(texto.trim());
      setPage(1);
      setSeleccion({});
    }, DEMORA_BUSQUEDA);
    return () => clearTimeout(t);
  }, [texto]);

  const cargar = useCallback(async () => {
    const id = ++pedido.current;
    setState((prev) => ({ loading: true, error: "", data: prev.data }));
    try {
      const res = await api.list({ q, page, ...(cumpleMes ? { birthdayMonth: new Date().getMonth() + 1 } : {}), ...(duplicados ? { duplicates: 1 } : {}), ...(archivados ? { archived: true } : {}), ...(consent ? { consent } : {}) });
      const info = infoDe(res);
      if (!info || !Array.isArray(info.items)) throw new Error("Invalid clients response");
      if (id === pedido.current) setState({ loading: false, error: "", data: info });
    } catch (e) {
      if (id === pedido.current) setState({ loading: false, error: errorDeApi(e, "No pudimos cargar los clientes. Probá de nuevo.").message, data: null });
    }
  }, [api, q, page, cumpleMes, duplicados, archivados, consent]);

  useEffect(() => {
    cargar();
  }, [cargar, retry, refreshKey]);

  const { loading, error, data } = state;
  const items = data?.items || [];
  const total = Number(data?.total) || 0;
  const pageSize = Number(data?.pageSize) || 25;
  const paginas = Math.max(1, Math.ceil(total / pageSize));
  const filtrando = Boolean(q) || cumpleMes || duplicados || Boolean(consent);
  const elegidas = Object.values(seleccion);

  const cambiarFiltro = (poner) => (e) => {
    poner(e.target.checked);
    setPage(1);
    setSeleccion({});
    setAviso("");
    setErrorFusion("");
  };

  const cambiarVista = (verArchivados) => {
    setArchivados(verArchivados);
    setDuplicados(false);
    setConsent("");
    setPage(1);
    setSeleccion({});
    setAviso("");
    setErrorFusion("");
  };

  // Archives (or unarchives) every selected client, one request each, then reloads and reports how it went.
  const archivarSeleccion = async () => {
    const llamar = archivados ? api.unarchive : api.archive;
    const verbo = archivados ? "desarchivar" : "archivar";
    const hecho = archivados ? "desarchivado" : "archivado";
    const ids = elegidas.map((c) => c._id);
    setAviso("");
    setErrorFusion("");
    setProcesando(true);
    const resultados = await Promise.allSettled(ids.map((id) => llamar(id)));
    const fallidos = resultados.filter((x) => x.status === "rejected");
    const ok = ids.length - fallidos.length;
    setProcesando(false);
    setSeleccion({});
    if (ok > 0) setAviso(ok === 1 ? `1 cliente ${hecho}.` : `${ok} clientes ${hecho}s.`);
    if (fallidos.length > 0) {
      const { status, message } = errorDeApi(fallidos[0].reason, `No pudimos ${verbo} a los clientes. Probá de nuevo.`);
      setErrorFusion(status === 403 ? `No tenés permiso para ${verbo} fichas de cliente.` : `No pudimos ${verbo} a ${fallidos.length} de ${ids.length} clientes. ${message}`);
    }
    setPage(1);
    setRetry((n) => n + 1);
  };

  const alternar = (c) =>
    setSeleccion((prev) => {
      const { [c._id]: ya, ...resto } = prev;
      return ya ? resto : { ...prev, [c._id]: c };
    });

  const fusionTerminada = (mensaje, { recargar, error: fallo = "" } = {}) => {
    setFusionando(false);
    setSeleccion({});
    setAviso(mensaje);
    setErrorFusion(fallo);
    if (recargar) {
      setPage(1);
      setRetry((n) => n + 1);
    }
  };

  return (
    <div className="fc-page">
      {archivados && (
        <div className="fc-page__head">
          <button type="button" className="zr-btn zr-btn--soft" onClick={() => cambiarVista(false)}>Volver a clientes</button>
          <h2>Archivados</h2>
        </div>
      )}
      <div className="fc-toolbar">
        <input
          type="search"
          className="zr-input"
          placeholder="Buscar por nombre, teléfono, email o DNI"
          aria-label="Buscar clientes"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <label className="fc-toggle">
          <input
            type="checkbox"
            checked={cumpleMes}
            onChange={cambiarFiltro(setCumpleMes)}
          />
          Cumplen años este mes
        </label>
        <label className="fc-toggle">
          Novedades
          <select
            aria-label="Filtrar por novedades"
            value={consent}
            onChange={(e) => {
              setConsent(e.target.value);
              setPage(1);
              setSeleccion({});
              setAviso("");
              setErrorFusion("");
            }}
          >
            {CONSENT_FILTROS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>
        {!archivados && (
          <label className="fc-toggle">
            <input type="checkbox" checked={duplicados} onChange={cambiarFiltro(setDuplicados)} />
            Posibles duplicados
          </label>
        )}
      </div>

      {aviso && <p className="fc-hint" role="status">{aviso}</p>}
      {errorFusion && <div className="fc-error" role="alert">{errorFusion}</div>}

      {elegidas.length >= 1 && (
        <div className="fc-selbar">
          <span>{elegidas.length === 1 ? "1 seleccionada" : `${elegidas.length} seleccionadas`}</span>
          <span className="fc-selbar__buttons">
            {!archivados && elegidas.length >= 2 && (
              <button type="button" className="zr-btn zr-btn--dark" disabled={procesando} onClick={() => { setAviso(""); setErrorFusion(""); setFusionando(true); }}>Fusionar</button>
            )}
            <button type="button" className="zr-btn zr-btn--soft" disabled={procesando} onClick={archivarSeleccion}>
              {procesando ? (archivados ? "Desarchivando…" : "Archivando…") : archivados ? "Desarchivar" : "Archivar"}
            </button>
            <button type="button" className="zr-btn zr-btn--ghost" onClick={() => setSeleccion({})}>Cancelar</button>
          </span>
        </div>
      )}

      {error && (
        <div className="fc-error" role="alert">
          {error}
          <button type="button" className="zr-btn zr-btn--soft" onClick={() => setRetry((n) => n + 1)}>Reintentar</button>
        </div>
      )}

      {loading && !data && <SpinLoading />}

      {!archivados && !error && data && (
        <button type="button" className="fc-archived-entry" onClick={() => cambiarVista(true)}>
          <span>Archivados</span>
          {Number.isFinite(Number(data.archivedCount)) && data.archivedCount !== null && <span className="zr-badge">{Number(data.archivedCount)}</span>}
        </button>
      )}

      {!error && data && items.length === 0 && (
        <div className="zr-panel">
          <div className="fc-empty">
            {filtrando ? (
              <>
                <p>No encontramos clientes con ese filtro.</p>
                <button
                  type="button"
                  className="zr-btn zr-btn--soft"
                  onClick={() => {
                    setTexto("");
                    setQ("");
                    setCumpleMes(false);
                    setDuplicados(false);
                    setConsent("");
                    setSeleccion({});
                    setPage(1);
                  }}
                >
                  Limpiar filtros
                </button>
              </>
            ) : (
              archivados ? (
                <p>No hay clientes archivados.</p>
              ) : (
              <>
                <p>Todavía no hay clientes con ficha.</p>
                <p>Abrí un turno en la agenda y tocá "Ver ficha del cliente" para crear la primera.</p>
              </>
              )
            )}
          </div>
        </div>
      )}

      {data && items.length > 0 && (
        <>
        <div className="fc-list-head" aria-hidden="true">
          <span>Cliente</span>
          <span>Contacto</span>
          <span>Último turno / visita</span>
          <span>Turnos / asistencias</span>
        </div>
        <ul className="fc-list" aria-label="Clientes" aria-busy={loading}>
          {items.map((c, i) => (
            <React.Fragment key={c._id}>
              {duplicados && c.duplicateGroup != null && c.duplicateGroup !== items[i - 1]?.duplicateGroup && (
                <li className="fc-group" aria-hidden="true">Mismo teléfono o email</li>
              )}
              <li className="fc-item">
                <label className="fc-check">
                  <input type="checkbox" aria-label={`Seleccionar a ${c.name || "cliente sin nombre"}`} checked={Boolean(seleccion[c._id])} onChange={() => alternar(c)} />
                </label>
                <button type="button" className="fc-row" aria-label={`Abrir ficha de ${c.name || "cliente sin nombre"}`} onClick={() => onAbrir(c._id)}>
                  <span>
                    <span className="fc-row__name">{c.name || "Sin nombre"}</span>
                    {estadoConsent(c.marketingConsent) !== "unknown" && (
                      <span className="zr-badge" style={{ marginLeft: 6 }}>{CONSENT_LABEL[estadoConsent(c.marketingConsent)]}</span>
                    )}
                    {(c.tags || []).length > 0 && (
                      <span className="fc-tags">
                        {c.tags.map((t) => <span key={t} className="fc-tag">{t}</span>)}
                      </span>
                    )}
                  </span>
                  <span>
                    <span className="fc-row__sub">{telefonoParaMostrar(c.phone) || "Sin teléfono"}</span>
                    <br />
                    <span className="fc-row__sub">{c.email || "Sin email"}</span>
                  </span>
                  <span className="fc-row__meta fc-row__date">
                    <span>Último turno: {c.lastAppointmentAt ? fechaAR(c.lastAppointmentAt) : "—"}</span>
                    {c.lastAttendedAt && <><br /><span>Última visita: {fechaAR(c.lastAttendedAt)}</span></>}
                  </span>
                  <span className="fc-row__meta fc-row__count">
                    <span>{Number(c.appointmentsCount) === 1 ? "1 turno" : `${Number(c.appointmentsCount) || 0} turnos`}</span>
                    {c.attendedCount != null && <><br /><span>{Number(c.attendedCount) === 1 ? "1 asistencia" : `${Number(c.attendedCount) || 0} asistencias`}</span></>}
                  </span>
                </button>
              </li>
            </React.Fragment>
          ))}
        </ul>
        </>
      )}

      {data && total > 0 && (
        <div className="fc-pager">
          <span>{total === 1 ? "1 cliente" : `${total} clientes`} · Página {page} de {paginas}</span>
          <span className="fc-pager__buttons">
            <button type="button" className="zr-btn zr-btn--soft" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Anterior</button>
            <button type="button" className="zr-btn zr-btn--soft" disabled={page >= paginas || loading} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
          </span>
        </div>
      )}

      {fusionando && <FusionarFichasModal fichas={elegidas} onListo={fusionTerminada} onCerrar={() => setFusionando(false)} />}
    </div>
  );
};

export default ClientesLista;
